import { AssetReversableClassificationEvent } from '@assetaccountant/api/models/assets/asset-reversable-classification-event';
import { AssetReversableComponentEvent } from '@assetaccountant/api/models/assets/asset-reversable-component-event';
import { AssetReversableEvent } from '@assetaccountant/api/models/assets/asset-reversable-event';
import { AssetReversableRegimeEvent } from '@assetaccountant/api/models/assets/asset-reversable-regime-event';

function applyReversal(fullType: string, translate: (coreType: string) => string): string {
  if (/Reversal$/.test(fullType)) {
    const coreType = fullType.replace(/Reversal$/, '');
    return `Reversal (${translate(coreType)})`;
  }
  if (/^Reversed/.test(fullType)) {
    const coreType = fullType.replace(/^Reversed/, '');
    return `${translate(coreType)} (Reversed)`;
  }

  return translate(fullType);
}

export const translateTransactionTypeName = (
  details:
    | { eventType: string | null | undefined }
    | AssetReversableEvent
    | AssetReversableRegimeEvent
    | AssetReversableComponentEvent
    | AssetReversableClassificationEvent,
): string =>
  applyReversal((details.eventType || '').replace(/^Pool/, ''), coreType => {
    switch (coreType) {
      case 'DepreciationEstimated':
        return 'Depreciation (est)';

      case 'OpeningWrittenDownValue':
      case 'OpeningBalance':
        return 'Opening Balance';

      case 'PurchaseCapLimit':
        return 'Cap Limit';

      case 'AddToPool':
        return 'Add to Pool';
      case 'TransferToPool':
        return 'Transfer to Pool';

      case 'WriteOff':
      case 'AutomaticWriteOff':
        return 'Write Off';
      case 'QuantitySale':
        return 'Sale (Partial)';
      case 'QuantityWriteOff':
        return 'Write Off (Partial)';

      case 'AdditionBeforeOpeningBalance':
      case 'Addition':
        return 'Addition';
      case 'TransferBeforeOpeningBalance':
      case 'Transfer':
        return 'Transfer';

      case 'AssetQuantitySale':
        return 'Asset Sale (Partial)';
      case 'AssetQuantityWriteOff':
        return 'Asset Write Off (Partial)';

      case 'FieldValueAssignedToAsset':
        if (
          'classificationName' in details &&
          typeof details.classificationName === 'string' &&
          Array.isArray(details.classificationValues)
        ) {
          if (details.classificationValues.length === 1) {
            return `${details.classificationName} Added: "${details.classificationValues[0]}"`;
          } else if (details.classificationValues.length > 1) {
            return `${details.classificationName} Added: (Multiple)`;
          }
          return `${details.classificationName} Added`;
        }
        return 'Classification Added';
      case 'FieldValueUpdatedForAsset':
        if (
          'classificationName' in details &&
          typeof details.classificationName === 'string' &&
          Array.isArray(details.classificationValues)
        ) {
          if (details.classificationValues.length === 0) {
            return `${details.classificationName} Removed`;
          } else if (details.classificationValues.length === 1) {
            return `${details.classificationName} Updated: "${details.classificationValues[0]}"`;
          } else {
            return `${details.classificationName} Updated: (Multiple)`;
          }
        }
        return 'Classification Updated';

      case 'ComponentAdded':
        if ('assetName' in details) {
          return `Component Added: "${details.assetName}"`;
        }
        return 'Component Added';
      case 'ParentAdded':
        if ('assetName' in details) {
          return `Parent Added: "${details.assetName}"`;
        }
        return 'Parent Added';
      case 'ComponentRemoved':
        if ('assetName' in details) {
          return `Component Removed: "${details.assetName}"`;
        }
        return 'Component Removed';
      case 'ParentRemoved':
        if ('assetName' in details) {
          return `Parent Removed: "${details.assetName}"`;
        }
        return 'Parent Removed';

      case 'AssetGroupAssignedToAsset':
        return 'Asset Group Assignment';

      case 'EndAdjustment':
      case 'StartAdjustment':
        return 'Adjustment';

      default:
        return coreType.replace(/([a-z])([A-Z])/g, '$1 $2');
    }
  });
